// src/pages/Admin/views/verification-rules/BannedIPsPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  listBannedIPs,
  batchUpdateBannedIPs
} from './verificationRulesApi'; // Ensure this path is correct
import CrudTable from '../../../../components/Admin/VerificationRules/CrudTable'; // Adjust path
import { formatDate, formatDateTime } from '../../../../utility/dateFormatter'; // Adjust path
import { Button } from '../../../../components/ui/button'; // Adjust path
import { useToast } from '../../../../hooks/use-toast';

// Basic IP Address validation (IPv4 and a simplified IPv6 check)
// For more robust validation, consider a library or a more comprehensive regex.
const validateIPAddress = (ipaddress) => {
  const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  // This is a very basic check for IPv6, not fully compliant with all edge cases (e.g. shortened forms)
  // Pydantic's IPvAnyAddress is more robust on the backend.
  const ipv6Regex = /(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:(:(:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))/;
  return ipv4Regex.test(ipaddress) || ipv6Regex.test(ipaddress);
};


const BannedIPsPage = () => {
  const [allBannedIPs, setAllBannedIPs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [stagedChanges, setStagedChanges] = useState([]);
  const { toast } = useToast();

  const [isAdding, setIsAdding] = useState(false);
  const [newItemData, setNewItemData] = useState({
    ip_address: '', // Changed
    reason: '',
    is_fatal: true,
    is_active: true,
    expires_at: '',
  });

  const [editingItemId, setEditingItemId] = useState(null);
  const [currentItemDataForEdit, setCurrentItemDataForEdit] = useState(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await listBannedIPs(); // Changed
      setAllBannedIPs(response.data || []); // Changed
      setStagedChanges([]);
    } catch (err) {
      let errorMessage = "Failed to fetch banned IPs."; // Changed
      if (err.response && err.response.data && err.response.data.detail) {
        errorMessage = typeof err.response.data.detail === 'string' ? err.response.data.detail : JSON.stringify(err.response.data.detail);
      } else if (err.message) {
        errorMessage = `Failed to fetch banned IPs: ${err.message}`; // Changed
      }
      setError(errorMessage);
      
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error'
      })
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Effect to clear edit mode if the item is removed/not found
  useEffect(() => {
    if (editingItemId && !allBannedIPs.find(item => (item.id || item.tempId) === editingItemId)) {
      handleCancelEdit();
    }
  }, [allBannedIPs, editingItemId]);


  const stageChange = (itemDataWithChange) => {
    setStagedChanges(prevChanges => {
      const keyOfChangedItem = itemDataWithChange.id || itemDataWithChange.tempId;
      let itemExistsInStaged = false;
      const updatedChanges = prevChanges.map(existingChange => {
        const keyOfExistingChange = existingChange.id || existingChange.tempId;
        if (keyOfExistingChange === keyOfChangedItem) {
          itemExistsInStaged = true;
          return { ...existingChange, ...itemDataWithChange };
        }
        return existingChange;
      });
      if (!itemExistsInStaged) {
        return [...updatedChanges, { ...itemDataWithChange }];
      }
      return updatedChanges;
    });
  };

  const handleEditClick = (item) => {
    const currentItemState = allBannedIPs.find(i => (i.id || i.tempId) === (item.id || item.tempId)) || item;
    if (editingItemId === (currentItemState.id || currentItemState.tempId)) {
      handleCancelEdit();
    } else {
      setEditingItemId(currentItemState.id || currentItemState.tempId);
      const dataForEditing = {
        ...currentItemState,
        expires_at: currentItemState.expires_at ? formatDate(currentItemState.expires_at) : '',
      };
      const stagedVersion = stagedChanges.find(sc => (sc.id || sc.tempId) === (currentItemState.id || currentItemState.tempId));
      if (stagedVersion) {
        setCurrentItemDataForEdit({
          ...dataForEditing,
          ...stagedVersion,
          expires_at: stagedVersion.expires_at ? formatDate(stagedVersion.expires_at) : '',
        });
      } else {
        setCurrentItemDataForEdit(dataForEditing);
      }
    }
    setError(null);
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setCurrentItemDataForEdit(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const L_value = type === 'checkbox' ? checked : value;
    if (editingItemId) {
      setCurrentItemDataForEdit(prev => ({ ...prev, [name]: L_value }));
    } else if (isAdding) {
      setNewItemData(prev => ({ ...prev, [name]: L_value }));
    }
  };

  const applyRowEditChanges = () => {
    if (!editingItemId || !currentItemDataForEdit) return;
    const ipAddressInput = currentItemDataForEdit.ip_address.trim();
    if (!ipAddressInput) {
      
      toast({
        title: 'Error',
        description: "IP address cannot be empty when editing.",
        status: 'error'
      })
      return;
    }
    if (!validateIPAddress(ipAddressInput)) {
      toast({
        title: 'Error',
        description: "Edited IP address is not a valid format.",
        status: 'error'
      })
      return;
    }
    setError(null);

    const editedData = { ...currentItemDataForEdit, ip_address: ipAddressInput };
    editedData.isNew = !!(editedData.tempId && !editedData.id);
    if (editedData.id && typeof editedData.id === 'number') {
      delete editedData.tempId;
    }
    setAllBannedIPs(prevAll => // Changed
      prevAll.map(ip => // Changed
        (ip.id === editingItemId || ip.tempId === editingItemId)
          ? { ...ip, ...editedData }
          : ip
      )
    );
    stageChange(editedData);
    
  };

  const handleAddNewRowToList = () => {
    const ipAddressInput = newItemData.ip_address.trim();
    if (!ipAddressInput) {
      
      toast({
        title: 'Error',
        description: "IP address cannot be empty when adding.",
        status: 'error'
      })
      return;
    }
    if (!validateIPAddress(ipAddressInput)) {
      toast({
        title: 'Error',
        description: "Please enter a valid IP address (IPv4 or IPv6).",
        status: 'error'
      })
      return;
    }
    setError(null);

    const tempId = `temp-${Date.now()}`;
    const newIPEntry = { // Changed
      ...newItemData,
      ip_address: ipAddressInput, // Use trimmed and validated
      tempId: tempId,
      isNew: true,
      expires_at: newItemData.expires_at || null,
    };
    delete newIPEntry.id;

    setAllBannedIPs(prev => [newIPEntry, ...prev]); // Changed
    stageChange(newIPEntry);

    setNewItemData({ ip_address: '', reason: '', is_fatal: true, is_active: true, expires_at: '' }); // Changed
    setIsAdding(false);
    
  };

  const handleDeleteOrDeactivate = (itemToToggle) => {
    const itemCurrentState = allBannedIPs.find(i => (i.id || i.tempId) === (itemToToggle.id || itemToToggle.tempId)) || itemToToggle; // Changed
    if (window.confirm(`Are you sure you want to ${itemCurrentState.is_active ? 'deactivate' : 'activate'} IP "${itemCurrentState.ip_address || 'this item'}"? This will be saved on "Save All".`)) { // Changed
      setError(null);
      const newActiveState = !itemCurrentState.is_active;
      setAllBannedIPs(prevAllIPs => // Changed
        prevAllIPs.map(ip => { // Changed
          const ipKey = ip.id || ip.tempId;
          const itemToToggleKey = itemCurrentState.id || itemCurrentState.tempId;
          if (ipKey === itemToToggleKey) {
            return { ...ip, is_active: newActiveState };
          }
          return ip;
        })
      );
      const changeToStage = {
        ...itemCurrentState,
        ip_address: itemCurrentState.ip_address.trim(), // Ensure IP is trimmed
        is_active: newActiveState,
        isNew: !itemCurrentState.id,
        expires_at: itemCurrentState.expires_at ? formatDate(itemCurrentState.expires_at) : null,
      };
      if (changeToStage.isNew) delete changeToStage.id;
      else delete changeToStage.tempId;
      stageChange(changeToStage);
      if (editingItemId === (itemCurrentState.id || itemCurrentState.tempId)) {
        setCurrentItemDataForEdit(prev => ({ ...prev, is_active: newActiveState }));
      }
      
    }
  };

  const handleSaveAll = async () => {
    if (editingItemId && currentItemDataForEdit) {
      applyRowEditChanges(); // This will also validate the IP being edited
      // Check if applyRowEditChanges set an error (e.g. validation failed)
      if (error) { // Assuming 'error' state is set by applyRowEditChanges on validation failure
          toast({
            title: 'Error',
            description: error,
            status: 'error'
          })
          return;
      }
      handleCancelEdit();
    }
    if (stagedChanges.length === 0) {
      return;
    }
    setIsLoading(true);
    setError(null);
    const payload = stagedChanges.map(change => {
      const { isNew, tempId, _isDeleted, created_at, updated_at, id, ip_address, ...itemSpecificData } = change;
      const backendItem = { ...itemSpecificData };
      backendItem.ip_address = ip_address.trim(); // Ensure IP is trimmed for payload
      backendItem.expires_at = backendItem.expires_at ? formatDate(backendItem.expires_at) : null;
      if (!(isNew || !id || typeof id !== 'number')) { // Only add ID if it's an existing item with a numeric ID
        backendItem.id = id;
      }
      return backendItem;
    });

    try {
      // console.log("Sending Banned IPs to batch update:", JSON.stringify(payload, null, 2));
      const response = await batchUpdateBannedIPs(payload); // Changed
      toast({
        variant: 'success',
        title: 'success',
        describtion: `Batch update successful! ${response.data?.length || 0} IPs processed.`,
      })
      setStagedChanges([]);
      fetchData();
    } catch (err) {
      let errorMessage = "An unexpected error occurred during IP batch update."; // Changed
      if (err.response && err.response.data) { /* ... (error parsing) ... */ 
        const errorData = err.response.data;
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') errorMessage = errorData.detail;
          else if (Array.isArray(errorData.detail)) errorMessage = errorData.detail.map(e => `${e.loc ? e.loc.join(' -> ') + ': ' : ''}${e.msg}`).join('\n');
          else if (typeof errorData.detail === 'object') errorMessage = JSON.stringify(errorData.detail);
        } else if (typeof errorData === 'object' && Object.keys(errorData).length > 0) errorMessage = errorData.message || JSON.stringify(errorData);
        else if (typeof errorData === 'string') errorMessage = errorData;
      } else if (err.message) {
        errorMessage = `Batch update failed: ${err.message}`;
      }
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error'
      })
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [ // Adapted for IP Addresses
    {
      key: 'ip_address', header: 'IP Address', // Changed
      render: (item) => editingItemId === (item.id || item.tempId) ?
        <input type="text" name="ip_address" value={currentItemDataForEdit?.ip_address || ''} onChange={handleInputChange} className="p-2 border border-gray-300 rounded-md w-full font-mono focus:ring-blue-500 focus:border-blue-500 text-base"/> : // font-mono for IPs
        <span className="text-sm font-mono">{item.ip_address}</span> // Changed, font-mono
    },
    {
      key: 'reason', header: 'Reason',
      render: (item) => editingItemId === (item.id || item.tempId) ?
        <input type="text" name="reason" value={currentItemDataForEdit?.reason || ''} onChange={handleInputChange} className="p-2 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500 text-base"/> :
        <span className="text-sm">{item.reason || '-'}</span>
    },
    {
      key: 'is_fatal', header: 'Fatal?',
      render: (item) => editingItemId === (item.id || item.tempId) ?
        <input type="checkbox" name="is_fatal" checked={!!currentItemDataForEdit?.is_fatal} onChange={handleInputChange} className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"/> :
        <span className="text-sm">{item.is_fatal ? 'Yes' : 'No'}</span>
    },
    {
      key: 'is_active', header: 'Status',
      render: (item) => editingItemId === (item.id || item.tempId) ?
        ( <label className="flex items-center text-sm">
            <input type="checkbox" name="is_active" checked={!!currentItemDataForEdit?.is_active} onChange={handleInputChange} className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"/>
            {currentItemDataForEdit?.is_active ? "Active" : "Inactive"}
          </label>
        ) :
        <span className={`text-sm font-semibold px-2 py-1 rounded-full ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{item.is_active ? 'Active' : 'Inactive'}</span>
    },
    {
      key: 'expires_at', header: 'Expires At',
      render: (item) => editingItemId === (item.id || item.tempId) ?
        <input type="date" name="expires_at" value={currentItemDataForEdit?.expires_at || ''} onChange={handleInputChange} className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-base"/> :
        <span className="text-sm">{item.expires_at ? formatDate(item.expires_at) : 'N/A'}</span>
    },
    { key: 'created_at', header: 'Created', render: (item) => <span className="text-sm">{item.isNew && !item.id ? <span className="italic text-gray-400">New (Unsaved)</span> : formatDateTime(item.created_at)}</span> },
    { key: 'updated_at', header: 'Updated', render: (item) => <span className="text-sm">{item.isNew && !item.id ? <span className="italic text-gray-400">N/A</span> : formatDateTime(item.updated_at)}</span> },
  ];

  const activeIPs = allBannedIPs.filter(ip => ip.is_active === true); // Changed
  const inactiveIPs = allBannedIPs.filter(ip => ip.is_active === false); // Changed
  const showSaveAllButton = stagedChanges.length > 0 || editingItemId !== null;

  return (
    <div className="p-4 md:p-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b border-gray-200">
        Manage Banned IP Addresses {/* Changed */}
      </h2>

      {isLoading && <p className="text-blue-600 animate-pulse">Loading data...</p>}
      {error && ( <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded whitespace-pre-line"> {typeof error === 'string' ? error : JSON.stringify(error, null, 2)} </div> )}

      {!isAdding && (
        <Button onClick={() => { setIsAdding(true); setError(null); handleCancelEdit(); }} variant="primary" size="md" className="mb-6">
          Add New IP Address {/* Changed */}
        </Button>
      )}

      {isAdding && (
        <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50 shadow">
          <h4 className="text-lg font-medium text-gray-700 mb-3">Add New Banned IP Address</h4> {/* Changed */}
           <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div className="flex flex-col">
              <label htmlFor="new_ip_address" className="mb-1 text-sm font-medium text-gray-600">IP Address*</label> {/* Changed */}
              <input id="new_ip_address" type="text" name="ip_address" placeholder="e.g., 192.168.1.1 or 2001:db8::1" value={newItemData.ip_address} onChange={handleInputChange} required className="p-2 border border-gray-300 rounded-md font-mono focus:ring-blue-500 focus:border-blue-500"/> {/* Changed */}
            </div>
            {/* ... Other fields: reason, expires_at, is_fatal, is_active ... */}
            <div className="flex flex-col">
              <label htmlFor="new_ip_reason" className="mb-1 text-sm font-medium text-gray-600">Reason</label>
              <input id="new_ip_reason" type="text" name="reason" placeholder="Reason for ban" value={newItemData.reason} onChange={handleInputChange} className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"/>
            </div>
            <div className="flex flex-col">
              <label htmlFor="new_ip_expires_at" className="mb-1 text-sm font-medium text-gray-600">Expires At</label>
              <input id="new_ip_expires_at" type="date" name="expires_at" value={newItemData.expires_at} onChange={handleInputChange} className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"/>
            </div>
             <div className="flex items-center space-x-6 mt-2 md:mt-0 md:pt-6">
              <label className="flex items-center text-sm font-medium text-gray-600">
                <input type="checkbox" name="is_fatal" checked={newItemData.is_fatal} onChange={handleInputChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"/> Is Fatal
              </label>
              <label className="flex items-center text-sm font-medium text-gray-600">
                <input type="checkbox" name="is_active" checked={newItemData.is_active} onChange={handleInputChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"/> Is Active
              </label>
            </div>
          </div>
          <div className="mt-4 flex gap-x-3">
            <Button onClick={handleAddNewRowToList} variant="green" size="md">Add to List & Stage</Button>
            <Button onClick={() => { setIsAdding(false); setError(null); }} variant="outline" size="md">Cancel</Button>
          </div>
        </div>
      )}

      {showSaveAllButton && (
        <div className="my-6 text-right">
          <Button onClick={handleSaveAll} variant="primary" size="lg" isLoading={isLoading} disabled={isLoading || (editingItemId && !currentItemDataForEdit?.ip_address)}> {/* Changed */}
            {isLoading ? 'Saving All...' : `Save All (${stagedChanges.length}) Changes`}
          </Button>
        </div>
      )}

      <section className="mb-8">
        <h3 className="text-xl font-semibold text-gray-700 mb-3">Active Banned IPs ({activeIPs.length})</h3> {/* Changed */}
        {activeIPs.length > 0 ? (
            <CrudTable items={activeIPs} columns={columns} onDeleteItem={handleDeleteOrDeactivate} editingItemId={editingItemId} onEditItem={handleEditClick} onCancelEdit={handleCancelEdit} onApplyEdit={applyRowEditChanges} /* ...styling props... */ />
        ) : (<p className="text-gray-500">No active banned IPs.</p>)} {/* Changed */}
      </section>

      <section>
        <h3 className="text-xl font-semibold text-gray-700 mb-3">Inactive Banned IPs ({inactiveIPs.length})</h3> {/* Changed */}
         {inactiveIPs.length > 0 ? (
            <CrudTable items={inactiveIPs} columns={columns} onDeleteItem={handleDeleteOrDeactivate} editingItemId={editingItemId} onEditItem={handleEditClick} onCancelEdit={handleCancelEdit} onApplyEdit={applyRowEditChanges} /* ...styling props... */ />
         ) : (<p className="text-gray-500">No inactive banned IPs.</p>)} {/* Changed */}
      </section>
    </div>
  );
};

export default BannedIPsPage;